/**
 * ChatDatabaseService — Dedicated IndexedDB client for mycrogym_chat
 *
 * Stores ALL decrypted chat data locally. The server (server_chat) only
 * ever receives and stores AES-256-GCM ciphertext — never plaintext.
 *
 * Object stores:
 *   conversations  — metadata + last message + unread count
 *   messages       — decrypted plaintext messages (local-only, never re-sent)
 *   message_keys   — encrypted key envelopes (raw, kept for re-decryption)
 *   device_keys    — other users' ECDH/RSA public keys (cache)
 *   my_key_pairs   — own keypair: public exportable, private non-extractable CryptoKey
 *   drafts         — unsent draft text per conversation
 *   sessions       — anonymous visitor session state (website plugin)
 */

import DBConfigService from '@/core/services/DBConfigService'

const DB_NAME    = 'mycrogym_chat'
const DB_VERSION = 2   // bumped: removed compound index unsupported in Safari

export interface ChatConversation {
  id: string
  companyid: string
  type: 'private' | 'group' | 'studio_channel' | 'anonymous'
  name: string | null
  unread: number
  lastMessage: string | null
  lastMsgAt: string | null
  options?: any
}

export interface DecryptedMessage {
  id: string
  conversationId: string
  senderid: string
  sendertype: 'user' | 'anonymous' | 'system'
  content_type: string
  body: string             // decrypted plaintext
  ref_type?: string | null
  ref_id?: string | null
  ref_label?: string | null
  ref_data?: any | null
  metadata?: any | null
  status: string
  created: string
}

export interface DeviceKey {
  deviceId: string
  userId: string
  publicKey: string        // Base64 SPKI
  keyVersion: number
}

export interface MyKeyPair {
  deviceId: string
  publicKeyB64: string     // exportable SPKI base64
  privateKey: CryptoKey    // non-extractable, stays in IDB
}

export class ChatDatabaseService {
  private static db: IDBDatabase | null = null

  // ── Open / init ────────────────────────────────────────────────────────────
  static open (): Promise<IDBDatabase | null> {
    if (this.db) return Promise.resolve(this.db)

    return new Promise((resolve) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION)

      req.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result

        // conversations
        if (!db.objectStoreNames.contains('conversations')) {
          const cs = db.createObjectStore('conversations', { keyPath: 'id' })
          cs.createIndex('companyid', 'companyid', { unique: false })
          cs.createIndex('lastMsgAt', 'lastMsgAt', { unique: false })
        }

        // messages
        if (!db.objectStoreNames.contains('messages')) {
          const ms = db.createObjectStore('messages', { keyPath: 'id' })
          ms.createIndex('conversationId', 'conversationId', { unique: false })
          // No compound index — Safari doesn't support multi-key IDBIndex reliably
        } else if (e.oldVersion < 2) {
          const upgradeTx = (e.target as IDBOpenDBRequest).transaction!
          const ms = upgradeTx.objectStore('messages')
          if (ms.indexNames.contains('conv_created')) ms.deleteIndex('conv_created')
        }

        if (!db.objectStoreNames.contains('message_keys')) db.createObjectStore('message_keys', { keyPath: 'id' })
        if (!db.objectStoreNames.contains('device_keys')) {
          const dk = db.createObjectStore('device_keys', { keyPath: 'deviceId' })
          dk.createIndex('userId', 'userId', { unique: false })
        }
        if (!db.objectStoreNames.contains('my_key_pairs')) db.createObjectStore('my_key_pairs', { keyPath: 'deviceId' })
        if (!db.objectStoreNames.contains('drafts'))       db.createObjectStore('drafts', { keyPath: 'conversationId' })
        if (!db.objectStoreNames.contains('sessions'))     db.createObjectStore('sessions', { keyPath: 'conversationId' })
      }

      req.onsuccess = (e) => {
        this.db = (e.target as IDBOpenDBRequest).result
        this.db.onversionchange = () => {
          this.db?.close()
          this.db = null
          console.warn('[ChatDB] DB version changed — connection closed to allow upgrade')
        }
        resolve(this.db)
      }
      req.onerror = () => {
        console.error('[ChatDB] IndexedDB open failed:', req.error)
        resolve(null)   // resolve null so callers can continue without cache
      }
      req.onblocked = () => {
        console.warn('[ChatDB] IndexedDB upgrade blocked — retrying in 2s')
        // Resolve null immediately so mount doesn't hang; retry open after delay
        resolve(null)
        setTimeout(() => { this.db = null; this.open() }, 2000)
      }
    })
  }

  private static async getDB (): Promise<IDBDatabase | null> {
    if (this.db) return this.db
    try { return await this.open() } catch { return null }
  }

  // ── Conversations ────────────────────────────────────────────────────────────
  static async saveConversation (conv: ChatConversation): Promise<void> {
    const db = await this.getDB()
    if (!db) return
    const tx = db.transaction('conversations', 'readwrite')
    const st = tx.objectStore('conversations')
    return new Promise((resolve, reject) => {
      const req = st.put(conv)
      req.onsuccess = () => resolve()
      req.onerror   = () => reject(req.error)
    })
  }

  static async getConversations (): Promise<ChatConversation[]> {
    const db = await this.getDB()
    if (!db) return []
    const tx = db.transaction('conversations', 'readonly')
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('conversations').getAll()
      req.onsuccess = () => resolve((req.result || []).sort((a, b) =>
        (b.lastMsgAt || '').localeCompare(a.lastMsgAt || '')))
      req.onerror = () => reject(req.error)
    })
  }

  static async incrementUnread (conversationId: string): Promise<void> {
    const db = await this.getDB()
    if (!db) return
    const tx = db.transaction('conversations', 'readwrite')
    const st = tx.objectStore('conversations')
    return new Promise((resolve, reject) => {
      const getReq = st.get(conversationId)
      getReq.onsuccess = () => {
        const conv = getReq.result
        if (!conv) { resolve(); return }
        conv.unread = (conv.unread || 0) + 1
        const putReq = st.put(conv)
        putReq.onsuccess = () => resolve()
        putReq.onerror   = () => reject(putReq.error)
      }
      getReq.onerror = () => reject(getReq.error)
    })
  }

  static async markRead (conversationId: string): Promise<void> {
    const db = await this.getDB()
    if (!db) return
    const tx = db.transaction('conversations', 'readwrite')
    const st = tx.objectStore('conversations')
    return new Promise((resolve, reject) => {
      const getReq = st.get(conversationId)
      getReq.onsuccess = () => {
        const conv = getReq.result
        if (!conv) { resolve(); return }
        conv.unread = 0
        const putReq = st.put(conv)
        putReq.onsuccess = () => resolve()
        putReq.onerror   = () => reject(putReq.error)
      }
      getReq.onerror = () => reject(getReq.error)
    })
  }

  static async updateConversationPreview (conversationId: string, lastMessage: string, lastMsgAt: string): Promise<void> {
    const db = await this.getDB()
    if (!db) return
    const tx = db.transaction('conversations', 'readwrite')
    const st = tx.objectStore('conversations')
    return new Promise((resolve, reject) => {
      const getReq = st.get(conversationId)
      getReq.onsuccess = () => {
        const conv = getReq.result
        if (!conv) { resolve(); return }
        conv.lastMessage = lastMessage
        conv.lastMsgAt   = lastMsgAt
        const putReq = st.put(conv)
        putReq.onsuccess = () => resolve()
        putReq.onerror   = () => reject(putReq.error)
      }
      getReq.onerror = () => reject(getReq.error)
    })
  }

  // ── Messages ────────────────────────────────────────────────────────────────
  static async saveMessage (msg: DecryptedMessage): Promise<void> {
    const db = await this.getDB()
    if (!db) return
    const tx = db.transaction('messages', 'readwrite')
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('messages').put(msg)
      req.onsuccess = () => resolve()
      req.onerror   = () => reject(req.error)
    })
  }

  static async getMessages (conversationId: string, limit = 50): Promise<DecryptedMessage[]> {
    const db = await this.getDB()
    if (!db) return []
    const tx  = db.transaction('messages', 'readonly')
    const idx = tx.objectStore('messages').index('conversationId')
    return new Promise((resolve, reject) => {
      const req = idx.getAll(conversationId)
      req.onsuccess = () => {
        const msgs = (req.result || [])
          .sort((a, b) => a.created.localeCompare(b.created))
          .slice(-limit)
        resolve(msgs)
      }
      req.onerror = () => reject(req.error)
    })
  }

  // ── Device keys ──────────────────────────────────────────────────────────────
  static async saveDeviceKey (key: DeviceKey): Promise<void> {
    const db = await this.getDB()
    if (!db) return
    const tx = db.transaction('device_keys', 'readwrite')
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('device_keys').put(key)
      req.onsuccess = () => resolve()
      req.onerror   = () => reject(req.error)
    })
  }

  static async getDeviceKeysByUser (userId: string): Promise<DeviceKey[]> {
    const db = await this.getDB()
    if (!db) return []
    const tx  = db.transaction('device_keys', 'readonly')
    const idx = tx.objectStore('device_keys').index('userId')
    return new Promise((resolve, reject) => {
      const req = idx.getAll(userId)
      req.onsuccess = () => resolve(req.result || [])
      req.onerror   = () => reject(req.error)
    })
  }

  // ── Own keypair ──────────────────────────────────────────────────────────────
  static async saveMyKeyPair (pair: MyKeyPair): Promise<void> {
    const db = await this.getDB()
    // Anders als Cache-Schreibvorgänge darf das nicht still verpuffen: ein nicht
    // gespeicherter privater Schlüssel ist verloren, sein öffentlicher Teil
    // würde aber trotzdem hochgeladen (siehe ChatServer.ensureDeviceKey).
    if (!db) throw new Error('[ChatDB] IndexedDB unavailable — key pair not stored')
    const tx = db.transaction('my_key_pairs', 'readwrite')
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('my_key_pairs').put(pair)
      req.onsuccess = () => resolve()
      req.onerror   = () => reject(req.error)
    })
  }

  /** false, wenn IndexedDB gerade nicht geöffnet werden kann (Safari: blocked/Fehler) */
  static async isAvailable (): Promise<boolean> {
    return !!(await this.getDB())
  }

  static async getMyKeyPair (deviceId: string): Promise<MyKeyPair | null> {
    const db = await this.getDB()
    if (!db) return null
    const tx = db.transaction('my_key_pairs', 'readonly')
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('my_key_pairs').get(deviceId)
      req.onsuccess = () => resolve(req.result || null)
      req.onerror   = () => reject(req.error)
    })
  }

  /**
   * Generate a new RSA-OAEP-2048 keypair for this device.
   * Private key is non-extractable — stays in IDB permanently.
   * Public key is returned as Base64 SPKI for upload to server_chat.
   */
  static async generateAndSaveKeyPair (deviceId: string): Promise<string> {
    const pair = await crypto.subtle.generateKey(
      { name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
      false,  // private key NOT extractable
      ['encrypt', 'decrypt']
    )
    const spki      = await crypto.subtle.exportKey('spki', pair.publicKey)
    const pubB64    = btoa(String.fromCharCode(...new Uint8Array(spki)))

    await this.saveMyKeyPair({ deviceId, publicKeyB64: pubB64, privateKey: pair.privateKey })
    return pubB64
  }

  // ── Drafts ────────────────────────────────────────────────────────────────────
  static async saveDraft (conversationId: string, text: string): Promise<void> {
    const db = await this.getDB()
    if (!db) return
    const tx = db.transaction('drafts', 'readwrite')
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('drafts').put({ conversationId, text, updated: new Date().toISOString() })
      req.onsuccess = () => resolve()
      req.onerror   = () => reject(req.error)
    })
  }

  static async getDraft (conversationId: string): Promise<string> {
    const db = await this.getDB()
    if (!db) return ''
    const tx = db.transaction('drafts', 'readonly')
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('drafts').get(conversationId)
      req.onsuccess = () => resolve(req.result?.text || '')
      req.onerror   = () => reject(req.error)
    })
  }

  // ── Crypto helpers (Option B: AES-256-GCM with per-conversation key) ─────────
  /**
   * Encrypt a plaintext message with the conversation's AES key.
   * Returns: { body (Base64 ciphertext), body_iv (hex), body_tag (hex) }
   */
  static async encryptMessage (plaintext: string, aesKey: CryptoKey): Promise<{ body: string; body_iv: string; body_tag: string }> {
    const iv  = crypto.getRandomValues(new Uint8Array(12))
    const enc = new TextEncoder()
    const ct  = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, enc.encode(plaintext))

    const ctArray   = new Uint8Array(ct)
    const ciphertext = ctArray.slice(0, -16)
    const tag        = ctArray.slice(-16)

    return {
      body:    btoa(String.fromCharCode(...ciphertext)),
      body_iv:  Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(''),
      body_tag: Array.from(tag).map(b => b.toString(16).padStart(2, '0')).join('')
    }
  }

  /**
   * Decrypt a message retrieved from server_chat.
   * The aesKey is obtained by decrypting chat_conversation_keys with the device private key.
   */
  static async decryptMessage (body: string, body_iv: string, body_tag: string, aesKey: CryptoKey): Promise<string> {
    const ivBytes  = new Uint8Array(body_iv.match(/.{2}/g)!.map(h => parseInt(h, 16)))
    const ctBytes  = Uint8Array.from(atob(body), c => c.charCodeAt(0))
    const tagBytes = new Uint8Array(body_tag.match(/.{2}/g)!.map(h => parseInt(h, 16)))
    const combined = new Uint8Array([...ctBytes, ...tagBytes])

    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBytes }, aesKey, combined)
    return new TextDecoder().decode(plain)
  }

  /**
   * Decrypt the wrapped AES conversation key using this device's RSA private key.
   * encrypted_key is the Base64 RSA-OAEP ciphertext stored in chat_conversation_keys.
   * extractable=true only for the device key-transfer flow — re-wrapping the key
   * for a new device requires exportKey('raw'), which non-extractable keys forbid.
   */
  static async unwrapConversationKey (encryptedKeyB64: string, privateKey: CryptoKey, extractable = false): Promise<CryptoKey> {
    const ciphertext = Uint8Array.from(atob(encryptedKeyB64), c => c.charCodeAt(0))
    const rawKey     = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, ciphertext)
    return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, extractable, ['encrypt', 'decrypt'])
  }

  // ── Studio auth token ─────────────────────────────────────────────────────────
  /**
   * Reads the JWT access token of the logged-in user (kl === true) from the
   * studio config DB (mycrogym_studio_config → user → at).
   *
   * MUST go through DBConfigService: `at` is encrypted at rest (EncryptedBlob)
   * once CryptoService is ready — a raw IndexedDB read would return the blob
   * object instead of the JWT string, which the chat server rejects as
   * AUTH_INVALID on every socket reconnect.
   */
  static async getStudioToken (): Promise<string> {
    try {
      await DBConfigService.bootstrapCrypto()
      const users = await DBConfigService.getalluserfromstorage()
      const row = users.find((u: any) => u.kl === true) || users[0]
      // Only a plain string is a usable JWT — an EncryptedBlob that could not
      // be decrypted must never be sent as a Bearer token.
      return typeof row?.at === 'string' ? row.at : ''
    } catch {
      return ''
    }
  }
}
